import { createFileRoute, Link } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { exportMyData } from "@/lib/export.functions";
import { Download, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/app/settings/export")({
  head: () => ({
    meta: buildAppMeta({
      title: "Export your data — CoachQuick",
      description: "Download a full archive of your clients, sessions, drills, invoices, and payments anytime.",
      path: "/app/settings/export",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/settings/export` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <ExportPage />
    </RequireAuth>
  ),
});

function ExportPage() {
  const run = useServerFn(exportMyData);
  const [busy, setBusy] = useState(false);

  async function download() {
    setBusy(true);
    try {
      const result = await run();
      // Decode base64 → Blob → trigger download
      const bin = atob(result.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: result.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const total = Object.values(result.counts).reduce((s, n) => s + n, 0);
      toast.success(`Exported ${total.toLocaleString()} records`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <div className="flex items-center gap-3">
        <Link to="/app/settings" className="text-sm text-muted-foreground hover:text-foreground">
          Settings
        </Link>
        <span className="text-muted-foreground">/</span>
        <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Export your data</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Download everything in your account as a ZIP archive.
      </p>

      <Card className="mt-6 p-5">
        <h2 className="text-lg font-semibold">What's included</h2>
        <ul className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
          <li>• Clients & relationships</li>
          <li>• Sessions, drills & summaries</li>
          <li>• Video clip references</li>
          <li>• Drill & technique library</li>
          <li>• Invoices & line items</li>
          <li>• Payments</li>
          <li>• Locations</li>
          <li>• Re-engagement nudges</li>
          <li>• Coach profile & preferences</li>
        </ul>
        <p className="mt-3 text-sm text-muted-foreground">
          You'll get a <span className="font-medium">CSV file per table</span> plus a single{" "}
          <span className="font-mono text-xs">full-export.json</span> archive for easy re-import.
        </p>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button onClick={download} disabled={busy} size="lg">
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {busy ? "Preparing export…" : "Download export (.zip)"}
          </Button>
          <p className="text-xs text-muted-foreground">
            Large accounts may take 10–20 seconds to package.
          </p>
        </div>
      </Card>

      <Card className="mt-4 border-success/30 bg-success/5 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 text-success" />
          <div className="text-sm">
            <p className="font-medium">Your data, always yours.</p>
            <p className="text-muted-foreground">
              Export remains available even if your trial ends or your subscription lapses. We
              never hold your coaching history hostage.
            </p>
          </div>
        </div>
      </Card>
    </AppShell>
  );
}
